import React, { useEffect, useMemo, useState } from "react";
import Router from "next/router";
import { Row, Col, Form, App, Input } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";

import UISwitch from "./UISwitch";
import UIAvatar from "./UIAvatar";
import UIDatePicker from "./UIDatePicker";
import UIFormButton from "./UIFormButton";
import UIMultiFields from "./UIMultiFields";
import UIFromHeading from "./UIFromHeading";
import UIImageUploder from "./UIImageUploder";
import UIFormTextArea from "./UIFormTextArea";
import UICountrySelect from "./UICountrySelect";
import UIFormAttachments from "./UIFormAttachments";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import UIFromTextInput, {
  OnBlurInputFields,
  OnChangeInputFields,
} from "./UIFromTextInput";
import UIRichTextEditor from "./UIRichTextEditor";
import UITimeZonePicker from "./UITimeZonePicker";
import UIResumeUploader from "./UIResumeUploader";
import UIFormTimePicker from "./UIFormTimePicker";
import UIFormSelectInput from "./UIFormSelectInput";
import { FeildTypes } from "../../../common/FeildTypes";
import UIAutoCompleteInput from "./UIAutoCompleteInput";
import UIAutoComplete from "./UIAutoComplete";
import UISelectCompleteInput from "./UISelectCompleteInput";
import UICheckbox from "./UICheckbox";
import { Mandantory } from "../../Mandantory";
import UIPhone from "./UIPhone";
import UIIconForm from "./UIIconForm";
import UICitySelect from "./UICitySelect";
import UIInputMask from "./UIInputMask";
import { GetCountriesCity } from "../../../pages/api/modules/common";
import GetTemplates from "@/components/employer/postajob/GetTemplates";
import UISkillsBox from "./UISkillsBox";

dayjs.extend(utc);

const FIELDS = {
  Heading: FeildTypes.Heading,
  TextInput: FeildTypes.TextInput,
  SelectInput: FeildTypes.SelectInput,
  SelectInputMultiple: FeildTypes.SelectInputMultiple,
  AutoCompleteInput: FeildTypes.AutoCompleteInput,
  Button: FeildTypes.Button,
  CancelButton: FeildTypes.CancelButton,
  TextArea: FeildTypes.TextArea,
  RichTextEditor: FeildTypes.RichTextEditor,
  Toggle: FeildTypes.Toggle,
  DatePicker: FeildTypes.DatePicker,
  Avatar: FeildTypes.Avatar,
  CountrySelect: FeildTypes.CountrySelect,
  ResumeUploader: FeildTypes.ResumeUploader,
  Table: FeildTypes.Table,
  TimePicker: FeildTypes.TimePicker,
  TimeZone: FeildTypes.TimeZone,
  ImagePicker: FeildTypes.ImagePicker,
  DateTimePicker: FeildTypes.DateTimePicker,
  SelectCompleteInput: FeildTypes.SelectCompleteInput,
  Checkbox: FeildTypes.Checkbox,
  MultiUserSelect: FeildTypes.MultiUserSelect,
  Attachments: FeildTypes.Attachments,
  Phone: FeildTypes.Phone,
  CitySelect: FeildTypes.CitySelect,
  Icon: FeildTypes.Icon,
  Hidden: FeildTypes.Hidden,
  InputMask: FeildTypes.InputMask,
  Template: FeildTypes.Template,
  Skillsbox: "Skillsbox",
};

const UIFrom = ({
  source = "",
  fields = [],
  requiredMark = true,
  onSubmitPress = () => {},
  onSavePress = () => {},
  saveLoading = {},
  submitLoading = {},
  showBackIcon,
  buttonLoding,
  showCancel,
  size = "default",
  onChange,
  onValuesChange = () => {},
  hideMultiFields,
  externalForm,
  renderAsForm = true,
  onCancelPress = () => {},
  onSwitch = () => {},
  fieldIndex,
}) => {
const [internalForm] = Form.useForm();
const usedForm = useMemo(() => externalForm || internalForm, [externalForm, internalForm]);
const watchedTable = Form.useWatch("table", usedForm) || {};


  const [autoFill, setAutoFill] = useState({});
  const [getCity, setGetCity] = useState({});
  const [isGeneratedJd, setIsGeneratedJd] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const { message } = App.useApp();
  const watchedIsGeneratedJd = Form.useWatch("IsGeneratedJd", usedForm);
  const isGenerated =
    watchedIsGeneratedJd === 1 || watchedIsGeneratedJd === true;
  const makeIndexedName = (id = "", idx) => {
    if (!idx) return id;
    if (/_\d+$/.test(id)) return id; 
    return `${id}${idx}`;
  };

  const getFormName = (item) => {
    const idx = fieldIndex || null;
    const raw = item?.ObjectId;

    if (item?.table) {
      const indexed = makeIndexedName(raw, idx);
      return ["table", indexed];
    }

    return raw;
  };

  const setValueByItem = (item, value) => {
    const name = getFormName(item);
    if (Array.isArray(name)) {
      const key = name[1];
      usedForm.setFieldsValue({
        table: {
          ...(usedForm.getFieldValue("table") || {}),
          [key]: value,
        },
      });

    } else {
      usedForm.setFieldsValue({ [name]: value });
    }
  };

  const findFieldById = (id) => {
    for (const row of fields || []) {
      const found = row?.ObjectId === id ? row : row?.Fields?.find((field) => field?.ObjectId === id);
      if (found) return found;
    }
    return null;
  };

  useEffect(() => {
    const templateField = fields?.find((f) => f.FieldType === FIELDS.Template);
    const initialTemplateId =
      templateField?.Result ?? templateField?.DefaultValue ?? null;

    if (templateField && initialTemplateId !== null) {
      setSelectedTemplate(initialTemplateId);
      usedForm.setFieldsValue({ [templateField.ObjectId]: initialTemplateId });
    }
  }, [fields]);

  const handleGetCity = async (data) => {
    const res = await GetCountriesCity(data);
    setGetCity({
      citiesData: res?.Data,
      citiesStatus: res?.Status,
    });
  };

  const autoFillData = (data) => setAutoFill(data);

  const renderItem = (item) => {
    const name = getFormName(item);
    const key = Array.isArray(name) ? name.join("_") : name;

    switch (item.FieldType) {
      case FIELDS.Heading:
        return (
          <UIFromHeading
            level={item?.level}
            type={item?.type}
            link={item?.link}
            className={item?.className}
          >
            {item.title}
          </UIFromHeading>
        );

      case FIELDS.Avatar:
        return (
          <UIAvatar
            label={item?.Title}
            name={name}
            required={item?.Mandatory}
            key={key}
            rules={item?.rules}
            requiredMark={item?.requiredMark}
            defaultValue={item?.DefaultValue}
            disabled={item?.Disabled}
            placeholder={item?.placeholder}
            onChange={(image) => setValueByItem(item, image)}
          />
        );

      case FIELDS.Hidden:
        return (
          <Form.Item
            name={name}
            initialValue={item?.DefaultValue}
            style={{ display: "none" }}
          >
            <Input type="hidden" />
          </Form.Item>
        );

      case FIELDS.Checkbox:
        return (
          <UICheckbox
            label={item?.Title}
            name={name}
            required={item?.Mandatory}
            key={key}
            rules={item?.rules}
            requiredMark={item?.requiredMark}
            defaultValue={item?.DefaultValue}
            disabled={item?.Disabled}
            placeholder={item?.placeholder}
            tooltip={item?.Tooltip}
            onSwitch={(checked) => {
              const v = checked ? 1 : 0;
              if (item?.ObjectId === "IsGeneratedJd") {
                setIsGeneratedJd(checked);
              }
              if (item?.table) {
                const indexed = makeIndexedName(item.ObjectId, fieldIndex);
                const prev = usedForm.getFieldValue("table") || {};
                const nextTable = { ...prev, [indexed]: v };
                if (/^hs_exp_cwo_\d+$/.test(indexed) && checked) {
                  nextTable[indexed.replace("hs_exp_cwo_", "hs_exp_to_")] = undefined;
                }
                if (/^hs_edu_cst_\d+$/.test(indexed) && checked) {
                  nextTable[indexed.replace("hs_edu_cst_", "hs_edu_to_")] = undefined;
                }
                usedForm.setFieldsValue({ table: nextTable });
                onChange?.({ target: { id: indexed, value: v } });
              } else {
                usedForm.setFieldsValue({ [item.ObjectId]: v });
              }
              if (item?.Target?.Action === "SelectAll" && item?.Target?.TargetId) {
                const target = findFieldById(item.Target.TargetId);
                usedForm.setFieldsValue({
                  [item.Target.TargetId]: checked ? Object.keys(target?.Values || {}) : [],
                });
              }
              onSwitch?.(checked, item?.Target?.TargetId);
            }}
          />
        );
      case FIELDS.Toggle:
        return (
          <UISwitch
            name={name}
            label={<Mandantory label={item?.Title} show={item?.Mandatory} />}
            required={item?.Mandatory}
            defaultValue={item?.DefaultValue}
            key={key}
            rules={item?.rules}
            onSwitch={(checked) => {
              const v = checked ? 1 : 0;
              if (item?.table) {
                const indexed = makeIndexedName(item.ObjectId, fieldIndex);
                const prev = usedForm.getFieldValue("table") || {};
                usedForm.setFieldsValue({ table: { ...prev, [indexed]: v } });
                onChange?.({ target: { id: indexed, value: v } });
              } else {
                usedForm.setFieldsValue({ [item.ObjectId]: v });
              }
              const allValues = usedForm.getFieldsValue(true);
              onSwitch?.(checked, allValues, item?.Target?.TargetId);
            }}
          />
        );

      case FIELDS.TextInput:
        return (
          <UIFromTextInput
            label={<Mandantory label={item?.Title} show={item?.Mandatory} />}
            name={name}
            required={item?.Mandatory}
            key={key}
            rules={item?.rules}
            onChange={
              item?.OnChange &&
              OnChangeInputFields(item?.OnChangeParams?.Key, usedForm, item)
            }
            onBlur={
              item?.OnBlur &&
              OnBlurInputFields(item?.OnBlurParams?.Key, usedForm, item)
            }
            requiredMark={item?.requiredMark}
            defaultValue={item?.DefaultValue}
            disabled={item?.Disabled}
            placeholder={item?.placeholder}
            tooltip={item?.Tooltip?.Message}
            source= {source}
          />
        );

      case FIELDS.InputMask:
        return (
          <UIInputMask
            label={<Mandantory label={item?.Title} show={item?.Mandatory} />}
            name={name}
            required={item?.Mandatory}
            key={key}
            rules={item?.rules}
            requiredMark={item?.requiredMark}
            defaultValue={item?.DefaultValue}
            disabled={item?.Disabled}
            placeholder={item?.placeholder}
            tooltip={item?.Tooltip?.Message}
            mask={item?.Mask}
            maskChar={item?.MaskChar}
          />
        );

      case FIELDS.Phone:
        return (
          <UIPhone
            label={<Mandantory label={item?.Title} show={item?.Mandatory} />}
            name={name}
            required={item?.Mandatory}
            key={key}
            rules={item?.rules}
            requiredMark={item?.requiredMark}
            defaultValue={item?.DefaultValue}
            disabled={item?.Disabled}
            placeholder={item?.placeholder}
            tooltip={item?.Tooltip?.Message}
            onChange={(value, country, e, formattedValue) => {
              setValueByItem(item, formattedValue);
            }}
          />
        );

      case FIELDS.Icon:
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <UIIconForm
              name={name}
              key={key}
              defaultValue={item?.DefaultValue}
              Icon={item?.DefaultValue}
              size="25px"
              color="#666"
            />
          </div>
        );

      case FIELDS.MultiUserSelect:
        return (
          <UIAutoComplete
            source={source}
            FieldType={item?.FieldType}
            label={<Mandantory label={item?.Title} show={item?.Mandatory} />}
            name={name}
            required={item?.Mandatory}
            key={key}
            rules={item?.rules}
            requiredMark={item?.requiredMark}
            defaultValue={item?.DefaultValue}
            placeholder={item?.placeholder}
            option={item?.Values}
          />
        );

      case FIELDS.Table:
        return (
          <>
            {!hideMultiFields && (
              <UIMultiFields
                label={item?.Title}
                name={item?.ObjectId}
                required={item?.Mandatory}
                key={item?.ObjectId}
                rules={item?.rules}
                requiredMark={item?.requiredMark}
                defaultValue={item?.DefaultValue}
                disabled={item?.Disabled}
                placeholder={item?.placeholder}
                fields={item?.Fields}
                showForm={item?.ShowValues}
                onChange={(e) => usedForm.setFieldsValue({ [item.ObjectId]: e })}
              />
            )}
          </>
        );

      case FIELDS.ResumeUploader:
        if (item?.Multiple) {
          return (
            <UIFormAttachments
              label={<Mandantory label={item?.Title} show={item?.Mandatory} />}
              name={name}
              required={item?.Mandatory}
              key={key}
              rules={item?.rules}
              defaultValue={item?.DefaultValue}
              disabled={item?.Disabled}
              onChange={(file) => setValueByItem(item, file)}
              accept={item?.accept}
              multiple
              maxCount={item?.MaxCount || 5}
              resumeStyle
              uploadTitle={item?.Title || "Attachments"}
            />
          );
        }
        return (
          <UIResumeUploader
            label={
              item?.Title && (
                <Mandantory label={item?.Title} show={item?.Mandatory} />
              )
            }
            name={name}
            required={item?.Mandatory}
            key={key}
            rules={item?.rules}
            requiredMark={item?.requiredMark}
            defaultValue={item?.DefaultValue}
            disabled={item?.Disabled}
            placeholder={item?.placeholder}
            onChange={(file) => setValueByItem(item, file)}
          />
        );

      case FIELDS.CountrySelect:
        return (
          <UICountrySelect
            label={<Mandantory label={item?.Title} show={item?.Mandatory} />}
            name={name}
            required={item?.Mandatory}
            key={key}
            rules={item?.rules}
            requiredMark={item?.requiredMark}
            defaultValue={item?.DefaultValue}
            disabled={item?.Disabled}
            placeholder={item?.placeholder}
            onChange={(e) => {
              setValueByItem(item, e);
              handleGetCity(e);
            }}
            source={source}
          />
        );

      case FIELDS.CitySelect:
        return (
          <UICitySelect
            label={<Mandantory label={item?.Title} show={item?.Mandatory} />}
            name={name}
            required={item?.Mandatory}
            key={key}
            rules={item?.rules}
            requiredMark={item?.requiredMark}
            defaultValue={item?.DefaultValue}
            cities={getCity}
            disabled={item?.Disabled}
            placeholder={item?.placeholder}
            onChange={(e) => setValueByItem(item, e)}
            style={item?.style}
          />
        );

      case FIELDS.DatePicker:
      case FIELDS.DateTimePicker:
        const fieldKey = Array.isArray(name) ? name[1] : name;
        const isExpToDisabled =
          /^hs_exp_to_\d+$/.test(fieldKey) &&
          watchedTable[fieldKey.replace("hs_exp_to_", "hs_exp_cwo_")] == 1;
        const isEduToDisabled =
          /^hs_edu_to_\d+$/.test(fieldKey) &&
          watchedTable[fieldKey.replace("hs_edu_to_", "hs_edu_cst_")] == 1;

        return (
          <UIDatePicker
            fieldType={item.FieldType}
            label={<Mandantory label={item?.Title} show={item?.Mandatory} />}
            name={name}
            key={key}
            required={item?.Mandatory}
            rules={isExpToDisabled || isEduToDisabled ? [] : item?.rules}
            defaultValue={item?.DefaultValue}
            disabled={item?.Disabled || isExpToDisabled || isEduToDisabled}
            placeholder={item?.placeholder}
            disableprevdate={item?.DisablePrevDate}
            onChange={(val) => {
              if (item?.table) {
                onChange?.({ target: { id: item?.ObjectId, value: val } });
              }
            }}
          />
        );

      case FIELDS.SelectInput:
        return (
          <UIFormSelectInput
            label={<Mandantory label={item?.Title} show={item?.Mandatory} />}
            name={name}
            required={item?.Mandatory}
            key={key}
            rules={item?.rules}
            requiredMark={item?.requiredMark}
            defaultValue={item?.DefaultValue}
            placeholder={item?.placeholder}
            option={item?.Values}
            disabled={item?.Disabled}
            onChange={onChange}
            style={item?.style}
            source={source}
          />
        );

      case FIELDS.SelectCompleteInput:
        return (
          <UISelectCompleteInput
            label={<Mandantory label={item?.Title} show={item?.Mandatory} />}
            name={name}
            required={item?.Mandatory}
            key={key}
            rules={item?.rules}
            requiredMark={item?.requiredMark}
            defaultValue={item?.DefaultValue}
            placeholder={item?.placeholder}
            option={item?.Values}
            disabled={item?.Disabled}
          />
        );

      case FIELDS.TimePicker:
        return (
          <UIFormTimePicker
            label={<Mandantory label={item?.Title} show={item?.Mandatory} />}
            name={name}
            required={item?.Mandatory}
            key={key}
            rules={item?.rules}
            requiredMark={item?.requiredMark}
            defaultValue={item?.DefaultValue}
            placeholder={item?.placeholder}
            option={item?.Values}
          />
        );

      case FIELDS.ImagePicker:
        return (
          <UIImageUploder
            label={<Mandantory label={item?.Title} show={item?.Mandatory} />}
            name={name}
            required={item?.Mandatory}
            key={key}
            rules={item?.rules}
            requiredMark={item?.requiredMark}
            defaultValue={item?.DefaultValue}
            placeholder={item?.placeholder}
            option={item?.Values}
            onChange={(file) => setValueByItem(item, file)}
          />
        );

      case FIELDS.TimeZone:
        return (
          <UITimeZonePicker
            label={<Mandantory label={item?.Title} show={item?.Mandatory} />}
            name={name}
            required={item?.Mandatory}
            key={key}
            rules={item?.rules}
            requiredMark={item?.requiredMark}
            defaultValue={item?.DefaultValue}
            placeholder={item?.placeholder}
            option={item?.Values}
          />
        );

      case FIELDS.SelectInputMultiple:
        return (
          <UIFormSelectInput
            mode={item?.Mode || "tags"}
            label={<Mandantory label={item?.Title} show={item?.Mandatory} />}
            name={name}
            required={item?.Mandatory}
            key={key}
            rules={item?.rules}
            requiredMark={item?.requiredMark}
            defaultValue={item?.DefaultValue}
            placeholder={item?.placeholder}
            option={item?.Values}
            disabled={item?.Disabled}
            onChange={(value) => {
              onChange?.(value, item);
            }}
          />
        );

      case FIELDS.AutoCompleteInput:
        return (
          <UIAutoCompleteInput
            FieldType={item?.FieldType}
            label={<Mandantory label={item?.Title} show={item?.Mandatory} />}
            name={name}
            required={item?.Mandatory}
            key={key}
            rules={item?.rules}
            requiredMark={item?.requiredMark}
            defaultValue={item?.DefaultValue}
            placeholder={item?.placeholder}
            option={item?.Values}
            onGetData={autoFillData}
            disabled={item?.Disabled}
          />
        );

      case FIELDS.Button:
        return (
          <UIFormButton
            level={item?.level}
            type={item?.type}
            className={item?.className}
            full={item?.full}
            htmlType={item?.htmlType}
            loading={buttonLoding}
            style={item?.style}
          >
            {item.title}
          </UIFormButton>
        );

      case FIELDS.CancelButton:
        return showCancel === false ? null : (
          <UIFormButton
            level={item?.level}
            type={item?.type}
            className={item?.className}
            full={item?.full}
            htmlType={item?.htmlType}
            loading={item?.htmlType === "submit" ? buttonLoding : false}
            style={item?.style}
            onClick={onCancelPress ? onCancelPress : onCancel}
          >
            {item.title}
          </UIFormButton>
        );

      case FIELDS.TextArea:
        return (
          <UIFormTextArea
            label={<Mandantory label={item?.Title} show={item?.Mandatory} />}
            name={name}
            required={item?.Mandatory}
            key={key}
            rows={item?.Rows}
            rules={item?.rules}
            defaultValue={item?.DefaultValue}
            requiredMark={item?.requiredMark}
            placeholder={item?.placeholder}
            source={source}
          />
        );

      case FIELDS.Attachments:
        return (
          <UIFormAttachments
            label={<Mandantory label={item?.Title} show={item?.Mandatory} />}
            name={name}
            required={item?.Mandatory}
            key={key}
            rows={item?.Rows}
            rules={item?.rules}
            defaultValue={item?.DefaultValue}
            requiredMark={item?.requiredMark}
            placeholder={item?.placeholder}
            onChange={(file) => setValueByItem(item, file)}
            accept={item?.accept || "image/*,application/pdf"}
            multiple={item?.Multiple || false}
            maxCount={item?.MaxCount || 1}
          />
        );
      case FIELDS.Skillsbox:
        return (
          <UISkillsBox
            label={<Mandantory label={item?.Title} show={item?.Mandatory} />}
            name={item?.ObjectId}
            required={item?.Mandatory}
            key={item?.ObjectId}
            rules={item?.rules}
            defaultValue={item?.DefaultValue}
            option={item?.Values}
            disabled={item?.Disabled}
            placeholder={item?.placeholder}
          />
        );
      case FIELDS.RichTextEditor: {
        if (!isGenerated && item.ObjectId === "JobDescriptionJd") return null;
        const valueFromForm = (() => {
          try {
            if (Array.isArray(name)) {
              const tableObj = usedForm.getFieldValue("table") || {};
              return tableObj[name[1]];

            }
            return usedForm.getFieldValue(name);

          } catch {
            return undefined;
          }
        })();

        const resolvedValue =
          valueFromForm ?? item?.Result ?? item?.DefaultValue ?? "";
        const rteLoadKey = key;

        return (
          <UIRichTextEditor
            form={usedForm}
            defaultValue={resolvedValue}
            loadKey={rteLoadKey}
            label={<Mandantory label={item?.Title} show={item?.Mandatory} />}
            name={name}
            required={item?.Mandatory}
            key={key}
            rules={item?.rules}
            placeholder={item?.placeholder}
            onChange={onChange}
            validateStatus="error"
            help={item?.Error ? item?.Error : false}
            hasFeedback={true}
            disabled={item?.Disabled}
            maxChars={item?.ObjectId === "JobDescriptionJd" ? 2150 : undefined}
            showCharCount={item?.ObjectId === "JobDescriptionJd"}
            source={source}
          />
        );
      }


      case FIELDS.Template: {
        if (isGeneratedJd !== true && item.ObjectId === "TemplateId")
          return <div />;

        const initialTemplateId = item?.DefaultValue ?? null;

        return (
          <>
            <Form.Item
              name="TemplateId"
              initialValue={initialTemplateId}
              style={{ display: "none" }}
            >
              <Input type="hidden" />
            </Form.Item>

            <GetTemplates
              onLoad={(TemplateId) => {
                setSelectedTemplate(TemplateId);
                usedForm.setFieldsValue({
                  [item.ObjectId]: TemplateId || 0,
                  TemplateId: TemplateId || 0,
                });
              }}
              defaultSelectedId={initialTemplateId}
              selectedTemplate={selectedTemplate ?? initialTemplateId}
              setSelectedTemplate={(TemplateId) => {
                setSelectedTemplate(TemplateId);
                usedForm.setFieldsValue({
                  [item.ObjectId]: TemplateId || 0,
                  TemplateId: TemplateId || 0,
                });
              }}
            />
          </>
        );
      }

      default:
        return <div />;
    }
  };

  const onFinish = (values) => onSubmitPress(values);

  const onCancel = () => Router.back();

  const onFinishFailed = (data) => {
    const msg = data?.errorFields?.[0]?.errors;
    message.error(msg);
  };

  const content = (
    <>
      {showBackIcon && (
        <ArrowLeftOutlined
          onClick={onCancel}
          style={{ position: "absolute", right: 1, cursor: "pointer" }}
        />
      )}
     {fields.map((item, index) => (
        <Row
          key={index}
          gutter={source === "ApplyJob"
            ? { xs: 0, sm: 0, md: 12, lg: 12, xl: 12 }
            : { xs: 0, sm: 0, md: 24, lg: 24, xl: 24 }}
          className={`d-flex ${
            item?.className ? item?.className : "align-items-end"
          }`}
        >
          {item.FieldType === "RowFields" ? (
            item.Fields?.length === 2 ? (
              item.Fields?.map((it, idx2) => (
                <Col xs={24} sm={24} md={12} lg={12} xl={12} key={idx2}>
                  {renderItem(it)}
                </Col>
              ))
            ) : item.Fields?.length === 3 ? (
              item.Fields?.map((it, idx2) => (
                <Col xs={24} sm={24} md={8} lg={8} xl={8} key={idx2}>
                  {renderItem(it)}
                </Col>
              ))
            ) : item.Fields?.length === 4 ? (
              item.Fields?.map((it, idx2) => (
                <Col xs={24} sm={24} md={6} lg={6} xl={6} key={idx2}>
                  {renderItem(it)}
                </Col>
              ))
            ) : item.Fields?.length > 4 ? (
              item.Fields?.map((it, idx2) => (
                <Col xs={24} sm={24} md={12} lg={12} key={idx2}>
                  {renderItem(it)}
                </Col>
              ))
            ) : (
              <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                {renderItem(item.Fields?.[0])}
              </Col>
            )
          ) : item.FieldType === "ButtonsRow" ? (
            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
              <Row style={{ justifyContent: "center", display: "flex" }}>
                <div style={{ marginRight: 10 }}>
                  {renderItem(item.Fields?.[0])}
                </div>
                <div>{renderItem(item.Fields?.[1])}</div>
              </Row>
            </Col>
          ) : (
            <Col
              xs={item.responsive?.xs}
              sm={item.responsive?.sm}
              md={item.responsive?.md}
              lg={item.responsive?.lg}
              xl={item.responsive?.xl}
            >
              {renderItem(item)}
            </Col>
          )}
        </Row>
      ))}
    </>
  );

if (!renderAsForm) {
  return <>{content}</>;
}



  return (
    <Form
      form={usedForm}
      initialValues={{ table: {} }}
      onFinish={onFinish}
      layout="vertical"
      requiredMark={requiredMark}
      size={size}
      onChange={onChange}
      onValuesChange={(changedValues, allValues) => onValuesChange(changedValues, allValues, usedForm)}
      style={{ position: "relative" }}
      scrollToFirstError
      onFinishFailed={onFinishFailed}
    >
      {content}
    </Form>
  );
};

export default UIFrom;
